import { useState, useEffect } from 'react';
import { Box, Button, Container, Heading, Icon, Stack, StackSeparator, Text } from '@chakra-ui/react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams, useLoaderData } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { ContentType, BlockType, FieldOption, StaticField } from '@/lib/types';
import FormContent from './form-content';
import { STATIC_FIELDS } from '@/lib/constants';

interface ContentTypeField {
  id: number;
  referenceID: string;
  field_id: string;
  field_name: string;
  field_type: number;
  field_unique_name: string;
  field_options: FieldOption[];
  workspace_id: number;
  content_type_id: number;
  sort_order: number;
  properties: any;
}

export default function EditContent() {
  const fields = useLoaderData() as ContentTypeField[];
  const navigate = useNavigate();
  const [rootBlocks, setRootBlocks] = useState<ContentTypeField[]>([]);
  const [contentType, setContentType] = useState<ContentType>();
  const [contentData, setContentData] = useState<any>();
  const [contentName, setContentName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const { contentTypeId, id } = useParams<{
    contentTypeId: string;
    id: string;
  }>();

  useEffect(() => {
    async function fetchContentType() {
      if (!contentTypeId) return;

      try {
        const response = await api.get(`content-types/${contentTypeId}`);
        setContentType(response.data.data);
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to fetch content type',
          duration: 3000,
        });
      }
    }

    fetchContentType();
  }, [contentTypeId]);

  useEffect(() => {
    async function processFieldsAndFetchContent() {
      if (!fields?.length || !contentType || !id) return;
      setIsLoading(true);

      try {
        const processedBlocks: ContentTypeField[] = [];

        const initialSortedFields = fields.sort(
          (a: ContentTypeField, b: ContentTypeField) => a.sort_order - b.sort_order,
        );

        for (const field of initialSortedFields) {
          const processedField = { ...field, properties: { ...field.properties } };

          if (processedField.field_type === BlockType.Selection) {
            const sortedOptions = [...processedField.field_options].sort(
              (a: FieldOption, b: FieldOption) => a.sort_order - b.sort_order,
            );

            processedField.properties.options = sortedOptions.map((item: FieldOption) => ({
              label: item.option_label,
              value: item.option_value,
            }));
          }

          if (
            processedField.field_unique_name &&
            STATIC_FIELDS.includes(processedField.field_unique_name as StaticField)
          ) {
            processedField.referenceID = processedField.field_unique_name;
          }

          if (!['s_created_date', 's_is_public'].includes(processedField.field_unique_name)) {
            processedBlocks.push(processedField);
          }
        }

        const staticFieldsList: ContentTypeField[] = [];
        const sortableFieldsList: ContentTypeField[] = [];

        processedBlocks.forEach((field: ContentTypeField) => {
          if (STATIC_FIELDS.includes(field.field_unique_name as StaticField)) {
            staticFieldsList.push(field);
          } else {
            sortableFieldsList.push(field);
          }
        });

        const contentRes = await api.get(`contents/${id}`);
        if (contentRes.data.data) {
          const content = contentRes.data.data;
          const formattedContent = {
            s_name: { value: content.name },
            s_description: { value: content.description },
            s_icon: { value: content.icon },
            ...content.content,
          };

          setContentData(formattedContent);
          setContentName(content.name || '');
        }

        setRootBlocks([...staticFieldsList, ...sortableFieldsList]);
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to load content type',
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    }

    processFieldsAndFetchContent();
  }, [contentType, fields, id]);

  const typeName = contentType?.name || 'Content';

  return (
    <Container maxW='4xl' py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }}>
      <Stack mb={6} gap={2} align='flex-start'>
        <Button variant='ghost' size='sm' onClick={() => navigate(-1)}>
          <Icon as={ArrowLeftIcon} w={4} h={4} />
          Back
        </Button>
        <Heading
          as='h1'
          fontSize={{ base: '2xl', md: '3xl' }}
          fontWeight='bold'
          letterSpacing='-0.01em'
        >
          Edit {typeName}
        </Heading>
        {contentName && (
          <Text color='fg.muted' fontSize='sm' lineClamp={1}>
            {contentName}
          </Text>
        )}
      </Stack>

      <Box
        bg='bg.panel'
        borderWidth={1}
        borderColor='border'
        borderRadius='2xl'
        shadow='xs'
        p={{ base: 5, md: 8 }}
      >
        {isLoading ? (
          <Stack gap={5}>
            <Skeleton height='10' />
            <Skeleton height='6' width='40%' />
            <Skeleton height='32' />
            <Skeleton height='10' />
            <Skeleton height='24' />
          </Stack>
        ) : (
          <Stack gap={4} separator={<StackSeparator />}>
            <FormContent rootBlocks={rootBlocks} contentId={id} responses={contentData} />
          </Stack>
        )}
      </Box>
    </Container>
  );
}
